import { useLayoutEffect, useMemo, useRef, useState } from 'react';

import { EntityRefUtil, FORM_TYPE_LIMIT, FormType, IssueSource } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import placeholderLogo from '@/assets/placeholder-logo.png';
import ActivityIcon from '@/components/icons/activity';
import CloseIcon from '@/components/icons/close';
import FormChecklistIcon from '@/components/icons/form-checklist';
import LogoutIcon from '@/components/icons/logout';
import PlusIcon from '@/components/icons/plus';
import WarningIcon from '@/components/icons/warning';
import WarningTriangleIcon from '@/components/icons/warning-triangle';
import InspectionFormDrawer from '@/components/inspections/inspection-form-drawer';
import IssueDrawer from '@/components/issues/issue-drawer';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import InformationIcon from '@/legacy/components/icons/information';
import { cn } from '@/lib/utils';
import { getAssetByIdQueryOptions } from '@/queries/assets';
import { awaPublicWorkspaceKeys, awaUserKeys } from '@/queries/awa';
import { getAssetFormsByTypeQueryOptions } from '@/queries/forms';
import { AWAGlobalApiService } from '@/services/global-api/awa-global-api.service';
import { useAWAStore } from '@/stores/awa.store';
import { stripAWAPath } from '@/utils/awa';
import { convertLogoUrl } from '@/utils/image-url';

import { AWAAssetActivity } from './activity';
import { AWAAssetDetails } from './details';
import { AWAAssetInspectionTemplates } from './forms';
import { AWAAssetIssues } from './issues';

type TabValue = 'activity' | 'inspections' | 'issues' | 'overview';

interface FormMenuItemProps {
  form: { id: string; name: string };
  onSelect: () => void;
}

/**
 * A dropdown menu item that displays "Create {form.name}" with truncation.
 * Uses useLayoutEffect for synchronous DOM measurement to calculate truncation
 * before browser paint, avoiding visual flicker.
 */
function FormMenuItem({ form, onSelect }: FormMenuItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const fullText = `Create ${form.name}`;
  const [displayText, setDisplayText] = useState(fullText);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const calculateTruncation = () => {
      // Temporarily set full text to measure
      el.textContent = fullText;

      // Check if truncation is needed
      if (el.scrollHeight <= el.clientHeight) {
        setDisplayText(fullText);
        return;
      }

      // Simple truncation: trim characters until it fits
      let text = fullText;
      while (el.scrollHeight > el.clientHeight && text.length > 0) {
        text = text.slice(0, -1);
        el.textContent = text.trimEnd() + '...';
      }
      setDisplayText(text.trimEnd() + '...');
    };

    calculateTruncation();

    const resizeObserver = new ResizeObserver(calculateTruncation);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [fullText]);

  return (
    <DropdownMenuItem
      key={form.id}
      asChild
      onClick={onSelect}
      className="w-full py-0 hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent">
      <div className="flex items-center justify-end gap-2">
        <div
          ref={ref}
          className="text-default-text line-clamp-2 max-w-[calc(95lvw-100px)] min-w-[200px] rounded bg-white px-2 py-0 text-right text-xl font-medium shadow-md sm:max-w-[300px]">
          {displayText}
        </div>
        <Button variant="default" size="icon" className="bg-default-blue h-[65px] w-[65px] rounded-full">
          <FormChecklistIcon size={30} className="mt-[-2px]" />
        </Button>
      </div>
    </DropdownMenuItem>
  );
}

export function AWAAssetPage() {
  const { assetId, workspaceId } = useParams();
  const { workspace, setWorkspace, authTokens, setAuthTokens } = useAWAStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [isInspectionsDrawerOpen, setIsInspectionsDrawerOpen] = useState(false);
  const [isIssuesDrawerOpen, setIsIssuesDrawerOpen] = useState(false);
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabValue>('activity');
  const [selectedInspectionFormId, setSelectedInspectionFormId] = useState<string | null>(null);

  const { data: asset, isLoading: assetLoading, isError: assetFetchError } = useQuery(getAssetByIdQueryOptions({ assetId, isAWA: true }));

  const { data: formsData, isLoading: formsLoading } = useQuery({
    ...getAssetFormsByTypeQueryOptions({ assetId: assetId!, formType: FormType.InspectionForm, limit: FORM_TYPE_LIMIT[FormType.InspectionForm], isAWA: true }),
    refetchOnWindowFocus: false,
  });

  const fabInspectionForms = useMemo(() => {
    // If forms are loading or no forms are deployed (single or deployment rules), return empty array and menu option is hidden
    if (formsLoading || !formsData?.[0]) return [];
    return formsData;
  }, [formsData, formsLoading]);

  const tabs: { value: TabValue; label: string }[] = [
    {
      value: 'activity',
      label: 'Activity',
    },
    {
      value: 'inspections',
      label: 'Inspections',
    },
    {
      value: 'issues',
      label: 'Issues',
    },
    {
      value: 'overview',
      label: 'Overview',
    },
  ];

  const { mutateAsync: revokeToken } = useMutation({
    mutationFn: async (token: string) => {
      await AWAGlobalApiService.revokeRefreshToken(token);
    },
  });

  async function handleLogout() {
    queryClient.invalidateQueries({ queryKey: awaPublicWorkspaceKeys.all });
    queryClient.invalidateQueries({ queryKey: awaUserKeys.all });
    if (authTokens?.refreshToken) {
      await revokeToken(authTokens.refreshToken).catch(() => {
        console.warn('Failed to revoke refresh token');
      });
    }
    setWorkspace(null);
    setAuthTokens(null);
    navigate(`/auth/login?workspaceId=${workspaceId}&redirectTo=${stripAWAPath(location.pathname, true)}`);
  }

  return (
    <div className="flex w-full flex-col">
      {assetLoading || formsLoading ? (
        <div className="bg-grey-1 flex max-h-[184px] min-h-[160px] w-full flex-row items-start gap-[15px] p-6">
          <div className="flex-none">
            <div className="from-grey-3 via-grey-1 to-grey-3 h-[100px] w-[100px] rounded border-4 border-white bg-gradient-to-tl shadow-lg" />
          </div>
          <div className="flex h-[100px] w-[224px] flex-col items-start gap-2.5 p-1">
            <div className="flex-none">
              <div className="from-grey-3 via-grey-1 to-grey-3 h-[27px] w-[200px] rounded bg-gradient-to-tl" />
            </div>
            <div className="flex-none">
              <div className="from-grey-3 via-grey-1 to-grey-3 h-[27px] w-[150px] rounded bg-gradient-to-tl" />
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-grey-1 flex h-[155px] w-full flex-col items-center justify-center gap-[15px] p-6">
          <div className="grid w-full grid-cols-3 items-center gap-2">
            <div />
            <div className="flex items-center justify-center">
              <div className="flex h-[50px] max-w-[140px] flex-col items-center justify-center gap-5 rounded border-4 border-white bg-white shadow-lg">
                <img
                  src={workspace?.logoUrl ? convertLogoUrl(workspace.logoUrl) : placeholderLogo}
                  alt="Org Image"
                  className="h-[50px] w-full object-contain"
                />
              </div>
            </div>
            <div className="flex items-center justify-end">
              <button onClick={handleLogout}>
                <LogoutIcon size={36} />
              </button>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-2.5">
            <div className="text-default-text line-clamp-2 max-w-full flex-none text-xl font-bold">{asset ? EntityRefUtil.createAssetLabel(asset) : ''}</div>
            <div className="flex-none"></div>
          </div>
        </div>
      )}

      {!assetLoading && !assetFetchError && asset && assetId && (
        <>
          <div className="bg-grey-1 flex flex-col gap-4">
            <Tabs defaultValue="activity" onValueChange={(value) => setActiveTab(value as TabValue)}>
              <TabsList className="rounded-t-2 flex h-[102px] w-full flex-row items-center rounded-b-none bg-white p-0">
                {tabs.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    asChild
                    className="border-b-grey-3 data-[state=active]:border-b-default-blue cursor-pointer rounded-b-[0px] border-b-[6px] data-[state=active]:bg-white data-[state=active]:shadow-none"
                    value={tab.value}>
                    <div className="flex h-full w-full flex-col items-center p-0">
                      {match(tab.value as TabValue)
                        .with('activity', () => <ActivityIcon size={30} className="text-default-text mx-[8px] my-1" />)
                        .with('inspections', () => <FormChecklistIcon size={30} className="text-default-text mx-[8px] my-1" />)
                        .with('issues', () => <WarningTriangleIcon size={30} className="text-default-text mx-[8px] my-1" />)
                        .with('overview', () => <InformationIcon size={30} className="text-default-text mx-[8px] my-1" />)
                        .exhaustive()}
                      <div className="text-default-text text-sm font-normal">{tab.label}</div>
                    </div>
                  </TabsTrigger>
                ))}
              </TabsList>
              <TabsContent className="m-0 h-[calc(100dvh-262px)] overflow-y-auto p-0" value="activity">
                <AWAAssetActivity asset={asset} />
              </TabsContent>
              <TabsContent className="m-0 h-[calc(100dvh-262px)] overflow-y-auto p-0" value="inspections">
                <AWAAssetInspectionTemplates assetId={assetId} />
              </TabsContent>
              <TabsContent className="m-0 h-[calc(100dvh-262px)] overflow-y-auto p-0" value="issues">
                <AWAAssetIssues asset={asset} />
              </TabsContent>
              <TabsContent className="m-0 h-[calc(100dvh-262px)] overflow-y-auto p-0" value="overview">
                <AWAAssetDetails assetId={assetId} />
              </TabsContent>
            </Tabs>
          </div>

          {!assetLoading && !formsLoading && (
            <>
              {activeTab !== 'inspections' && (
                <>
                  {isFabMenuOpen && (
                    <div
                      className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80"
                      onClick={() => setIsFabMenuOpen(false)}
                    />
                  )}

                  <div className="fixed bottom-4 z-50 mb-[65px] h-0 w-full max-w-[414px] pr-4 sm:max-w-[800px]">
                    {activeTab === 'activity' && (
                      <DropdownMenu onOpenChange={setIsFabMenuOpen}>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="default"
                            size="icon"
                            className={cn(
                              `hover:bg-default-dark-green float-right h-[65px] w-[65px] rounded-full`,
                              isFabMenuOpen ? 'bg-default-blue' : 'bg-default-green',
                            )}>
                            {isFabMenuOpen ? <CloseIcon size={30} /> : <PlusIcon size={30} />}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                          align="end"
                          className="float-right mr-[-9px] mb-[15px] flex flex-col gap-4 border-0 bg-transparent p-0 shadow-none">
                          <DropdownMenuItem
                            asChild
                            onClick={() => setIsIssuesDrawerOpen(true)}
                            className="py-0 hover:bg-transparent focus:bg-transparent data-[highlighted]:bg-transparent">
                            <div className="flex w-full items-center justify-end gap-2">
                              <div className="text-default-text gap-1 rounded bg-white px-2 py-0 text-xl font-medium shadow-md">Create Issue</div>
                              <Button variant="default" size="icon" className="bg-default-blue h-[65px] w-[65px] rounded-full">
                                <WarningTriangleIcon size={30} className="mt-[-2px]" />
                              </Button>
                            </div>
                          </DropdownMenuItem>
                          {fabInspectionForms.map((form) => (
                            <FormMenuItem
                              key={form.id}
                              form={form}
                              onSelect={() => {
                                setSelectedInspectionFormId(form.id);
                                setIsInspectionsDrawerOpen(true);
                              }}
                            />
                          ))}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {activeTab === 'issues' && (
                      <Button
                        variant="default"
                        size="icon"
                        className={cn(`bg-default-green hover:bg-default-dark-green float-right h-[65px] w-[65px] rounded-full`)}
                        onClick={() => setIsIssuesDrawerOpen(true)}>
                        <PlusIcon size={30} />
                      </Button>
                    )}
                  </div>
                </>
              )}

              {selectedInspectionFormId && (
                <InspectionFormDrawer
                  formId={selectedInspectionFormId}
                  assetId={assetId}
                  open={isInspectionsDrawerOpen}
                  onOpenChange={setIsInspectionsDrawerOpen}
                />
              )}

              <IssueDrawer
                noTrigger
                asset={asset}
                open={isIssuesDrawerOpen}
                source={IssueSource.Manual}
                onOpenChange={setIsIssuesDrawerOpen}
                locationId={asset?.locationId ?? undefined}
              />
            </>
          )}
        </>
      )}

      <Dialog open={!assetLoading && assetFetchError && !asset}>
        <DialogContent className="w-[354px] rounded-md p-0 sm:w-[500px]">
          <VisuallyHidden>
            <DialogTitle>Error</DialogTitle>
          </VisuallyHidden>
          <DialogHeader>
            <DialogDescription className="flex flex-col items-center gap-2 pt-6">
              <WarningIcon size={30} className="text-default-red" />
              <div className="text-default-text text-xl font-medium">Unable to load the asset</div>
              <div className="text-default-text text-sm font-normal">An error has occurred with loading this asset.</div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mb-6 flex items-center sm:flex-col sm:space-x-0">
            <Button
              className="bg-default-blue mt-4 w-fit rounded-md p-2.5 text-sm font-medium"
              onClick={() => {
                location.reload();
              }}>
              Retry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
